import { useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { NumberStepper } from "@/components/design/NumberStepper";
import { Select } from "@/components/design/Select";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import {
  AssignQuestionnaireToGroupDocument,
  DeleteQuestionnaireGroupDocument,
  PublishQuestionnaireGroupDocument,
  SaveQuestionnaireGroupDocument,
  UnpublishQuestionnaireGroupDocument,
} from "@/graphql/generated/operations";
import type { FieldOption } from "@/lib/field-util";
import { ingestFiles } from "@/lib/file-ingest";
import {
  MAX_QUESTIONNAIRE_GROUP_IMAGE_BYTES,
  MB,
  RASTER_IMAGE_ACCEPT,
} from "@/lib/upload-limits";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { StatusDot } from "./StatusDot";
import {
  GROUP_DESCRIPTION_TARGET,
  MAX_POSITION,
  groupName,
  hasActiveMember,
  imageSrc,
  nextGroupPosition,
  reorderPlan,
} from "./questionnaire-groups";
import type { GroupRow, QuestionnaireRow } from "./questionnaire-groups";

// Group level of the questionnaire admin (genesis#183). Sits in the same
// editor pane as the questionnaire form, so the manager keeps one list and one
// detail column rather than growing a second surface.
//
// Two mutation families with different timing on purpose. The form fields
// (names, descriptions, position, artwork) are a draft that Save commits in
// one `saveQuestionnaireGroup`. Publishing, withdrawing and every membership
// change fire immediately: each is its own genesis mutation with its own
// consequence, and folding them into Save would hide that.

const inputCls =
  "w-full rounded-md bg-page px-3 py-2 text-[13px] leading-relaxed outline-none ring-1 ring-ink/10 transition focus:ring-accent/40";

interface PickedImage {
  name: string;
  mimeType: string;
  content: string;
}

/** The confirm that has to name where the answers go before a member moves. */
interface PendingAssign {
  questionnaireId: string;
  name: string;
  fromGroupName: string | null;
}

interface QuestionnaireGroupEditorProps {
  /** The persisted group, or null while creating one. */
  group: GroupRow | null;
  /** Members of this group in display order, every status included. */
  members: QuestionnaireRow[];
  /** Every questionnaire, for the "add a part" picker. */
  allQuestionnaires: QuestionnaireRow[];
  /** Every group, for naming the source of a move and for the next free position. */
  groups: GroupRow[];
  onSaved: (questionnaireGroupId: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
  /** A membership or status change landed; the manager refetches both lists. */
  onChanged: () => void;
  /**
   * Non-null while a list query has failed. Publish, withdraw and delete all
   * describe the members in their confirmation, and a failed list makes that
   * description a guess, so they wait rather than promise the wrong thing.
   */
  blockedReason: string | null;
}

export function QuestionnaireGroupEditor({
  group,
  members,
  allQuestionnaires,
  groups,
  onSaved,
  onDeleted,
  onCancel,
  onChanged,
  blockedReason,
}: QuestionnaireGroupEditorProps) {
  const intl = useIntl();
  const zoneRef = useRef<FileDropZoneHandle>(null);

  const [nameDe, setNameDe] = useState(group?.name.german ?? "");
  const [nameEn, setNameEn] = useState(group?.name.english ?? "");
  const [descriptionDe, setDescriptionDe] = useState(group?.description?.german ?? "");
  const [descriptionEn, setDescriptionEn] = useState(group?.description?.english ?? "");
  const [position, setPosition] = useState(group ? group.position : nextGroupPosition(groups));

  const [picked, setPicked] = useState<PickedImage | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [confirmingRemoveImage, setConfirmingRemoveImage] = useState(false);
  const [imageIssue, setImageIssue] = useState<string | null>(null);

  // A save that genesis answered with something other than the group.
  const [saveIssue, setSaveIssue] = useState<string | null>(null);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingUnpublish, setConfirmingUnpublish] = useState(false);
  const [confirmingEmptyPublish, setConfirmingEmptyPublish] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [pendingAssign, setPendingAssign] = useState<PendingAssign | null>(null);
  const [addSelection, setAddSelection] = useState<string | null>(null);

  const [save, { loading: saving, error: saveError }] = useMutation(SaveQuestionnaireGroupDocument);
  const [del, { loading: deleting, error: deleteError }] = useMutation(
    DeleteQuestionnaireGroupDocument,
  );
  const [publish, { loading: publishing, error: publishError }] = useMutation(
    PublishQuestionnaireGroupDocument,
  );
  const [unpublish, { loading: unpublishing, error: unpublishError }] = useMutation(
    UnpublishQuestionnaireGroupDocument,
  );
  const [assign, { loading: assigning, error: assignError }] = useMutation(
    AssignQuestionnaireToGroupDocument,
  );

  const busy = saving || deleting || publishing || unpublishing || assigning;
  const consequentialBlocked = blockedReason !== null;
  const valid = nameDe.trim().length > 0 && nameEn.trim().length > 0;
  const published = group?.status === "PUBLISHED";
  const existingImage = removeImage ? null : imageSrc(group?.image);
  const preview = picked ? `data:${picked.mimeType};base64,${picked.content}` : existingImage;

  const maxMb = Math.floor(MAX_QUESTIONNAIRE_GROUP_IMAGE_BYTES / MB);

  async function ingest(fileList: FileList | null) {
    try {
      const result = await ingestFiles(fileList, {
        maxBytes: MAX_QUESTIONNAIRE_GROUP_IMAGE_BYTES,
        accept: RASTER_IMAGE_ACCEPT,
      });
      const tooLarge = result.rejected.find((r) => r.reason === "tooLarge");
      const unsupported = result.rejected.find((r) => r.reason === "unsupportedType");
      if (tooLarge) {
        setImageIssue(
          intl.formatMessage(
            { id: "admin.groupImageTooLarge", defaultMessage: "Too large (max. {max} MB): {name}" },
            { max: maxMb, name: tooLarge.file.name },
          ),
        );
      } else if (unsupported) {
        setImageIssue(
          intl.formatMessage(
            { id: "admin.groupImageUnsupported", defaultMessage: "Unsupported file type: {name}" },
            { name: unsupported.file.name },
          ),
        );
      } else {
        setImageIssue(null);
      }
      const accepted = result.accepted[0];
      if (!accepted) return;
      setPicked({ name: accepted.name, mimeType: accepted.mimeType, content: accepted.content });
      setRemoveImage(false);
      setConfirmingRemoveImage(false);
    } catch {
      logApplicationFailure("admin.read-questionnaire-group-image");
    }
  }

  function dropImage() {
    setPicked(null);
    setRemoveImage(true);
    setConfirmingRemoveImage(false);
    setImageIssue(null);
  }

  async function submit() {
    setSaveIssue(null);
    try {
      const extension = picked?.name.includes(".") ? picked.name.split(".").pop() : null;
      const res = await save({
        variables: {
          input: {
            questionnaireGroupId: group?.questionnaireGroupId ?? null,
            nameDe: nameDe.trim(),
            nameEn: nameEn.trim(),
            descriptionDe: descriptionDe.trim() || null,
            descriptionEn: descriptionEn.trim() || null,
            position,
            image: picked
              ? { mimeType: picked.mimeType, fileExtension: extension, content: picked.content }
              : null,
            // Only meaningful without a replacement; genesis ignores it otherwise.
            removeImage: removeImage && !picked ? true : null,
          },
        },
      });
      const out = res.data?.saveQuestionnaireGroup;
      if (out?.__typename === "QuestionnaireGroup") {
        setPicked(null);
        setRemoveImage(false);
        setConfirmingRemoveImage(false);
        onSaved(out.questionnaireGroupId);
        return;
      }
      if (out?.__typename === "TooManyRecordsResult") {
        setSaveIssue(
          intl.formatMessage(
            {
              id: "admin.groupLimitReached",
              defaultMessage: "You have reached the maximum of {limit} groups.",
            },
            { limit: out.limit ?? "" },
          ),
        );
        return;
      }
      const failed = intl.formatMessage({
        id: "admin.groupSaveFailed",
        defaultMessage: "The group could not be saved.",
      });
      if (out?.__typename === "ValidateQuestionnaireGroupResult") {
        // A blank message renders as nothing, which would leave the refusal unexplained.
        setSaveIssue(out.issues[0]?.message.trim() || failed);
        return;
      }
      // Schema-illegal: the group is non-null. Nothing was written, so say so
      // rather than leave the editor looking saved.
      logApplicationFailure("admin.save-questionnaire-group");
      setSaveIssue(failed);
    } catch {
      logApplicationFailure("admin.save-questionnaire-group");
    }
  }

  async function runPublish() {
    if (!group || busy) return;
    setConfirmingEmptyPublish(false);
    try {
      await publish({ variables: { questionnaireGroupId: group.questionnaireGroupId } });
      onChanged();
    } catch {
      logApplicationFailure("admin.publish-questionnaire-group");
    }
  }

  async function runUnpublish() {
    if (!group || busy) return;
    setConfirmingUnpublish(false);
    try {
      await unpublish({ variables: { questionnaireGroupId: group.questionnaireGroupId } });
      onChanged();
    } catch {
      logApplicationFailure("admin.unpublish-questionnaire-group");
    }
  }

  async function runDelete() {
    if (!group || busy) return;
    setConfirmingDelete(false);
    try {
      await del({ variables: { questionnaireGroupId: group.questionnaireGroupId } });
      onDeleted();
    } catch {
      logApplicationFailure("admin.delete-questionnaire-group");
    }
  }

  async function runAssign(questionnaireId: string, groupId: string | null, memberPosition?: number) {
    await assign({
      variables: {
        input: {
          questionnaireId,
          questionnaireGroupId: groupId,
          position: memberPosition ?? null,
        },
      },
    });
  }

  async function confirmAssign() {
    if (!group || !pendingAssign || busy) return;
    const { questionnaireId } = pendingAssign;
    setPendingAssign(null);
    setAddSelection(null);
    try {
      await runAssign(questionnaireId, group.questionnaireGroupId);
      onChanged();
    } catch {
      logApplicationFailure("admin.assign-questionnaire-to-group");
    }
  }

  async function removeMember(questionnaireId: string) {
    setRemovingMemberId(null);
    try {
      await runAssign(questionnaireId, null);
      onChanged();
    } catch {
      logApplicationFailure("admin.assign-questionnaire-to-group");
    }
  }

  async function move(questionnaireGroupId: string, index: number, direction: -1 | 1) {
    const plan = reorderPlan(members, index, direction);
    if (plan.length === 0) return;
    try {
      for (const step of plan) {
        await runAssign(step.questionnaireId, questionnaireGroupId, step.position);
      }
    } catch {
      logApplicationFailure("admin.reorder-questionnaire-group");
    } finally {
      // A reorder is several mutations; if one fails the earlier ones already
      // landed, so the list has to be re-read either way.
      onChanged();
    }
  }

  const memberIds = new Set(members.map((m) => m.questionnaireId));
  const groupNameById = new Map(
    groups.map((g) => [g.questionnaireGroupId, groupName(g.name)] as const),
  );
  const assignable: FieldOption[] = allQuestionnaires
    .filter((q) => !memberIds.has(q.questionnaireId))
    .map((q) => {
      const from = q.questionnaireGroupId ? groupNameById.get(q.questionnaireGroupId) : null;
      return {
        value: q.questionnaireId,
        label: from
          ? intl.formatMessage(
              { id: "admin.inGroupOption", defaultMessage: "{name} (in {group})" },
              { name: q.name, group: from },
            )
          : q.name,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const errors = [saveError, deleteError, publishError, unpublishError, assignError].filter(
    (e) => e != null,
  );

  return (
    <div className="box bg-surface p-5 @sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-lg font-medium text-ink">
          {group
            ? groupName(group.name)
            : intl.formatMessage({ id: "admin.newGroupTitle", defaultMessage: "New group" })}
        </span>
        {group ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-ink/[0.06] px-2.5 py-1 text-[11px] text-ink/70">
              {published
                ? intl.formatMessage({ id: "admin.statusPublished", defaultMessage: "Published" })
                : intl.formatMessage({ id: "admin.statusDraft", defaultMessage: "Draft" })}
            </span>
            <span className="text-[11px] text-ink/45">
              {intl.formatMessage(
                {
                  id: "admin.groupItemCount",
                  defaultMessage: "{count, plural, one {# question} other {# questions}}",
                },
                { count: group.itemCount },
              )}
            </span>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2">
        <label className="block">
          <span className="field-label text-ink/55">
            {intl.formatMessage({ id: "admin.groupNameDeLabel", defaultMessage: "Name (German)" })}
          </span>
          <input
            className={`mt-1 ${inputCls}`}
            value={nameDe}
            onChange={(e) => setNameDe(e.target.value)}
            placeholder={intl.formatMessage({
              id: "admin.groupNamePlaceholder",
              defaultMessage: "e.g. Power Quiz",
            })}
          />
        </label>
        <label className="block">
          <span className="field-label text-ink/55">
            {intl.formatMessage({ id: "admin.groupNameEnLabel", defaultMessage: "Name (English)" })}
          </span>
          <input
            className={`mt-1 ${inputCls}`}
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder={intl.formatMessage({
              id: "admin.groupNamePlaceholder",
              defaultMessage: "e.g. Power Quiz",
            })}
          />
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 @sm:grid-cols-2">
        <label className="block">
          <span className="field-label text-ink/55">
            {intl.formatMessage({
              id: "admin.groupDescriptionDeLabel",
              defaultMessage: "Description (German)",
            })}
          </span>
          <textarea
            className={`mt-1 ${inputCls} min-h-[60px] resize-y`}
            value={descriptionDe}
            onChange={(e) => setDescriptionDe(e.target.value)}
          />
          <span className="field-meta mt-1 block text-ink/45">
            {descriptionDe.length} / {GROUP_DESCRIPTION_TARGET}
          </span>
        </label>
        <label className="block">
          <span className="field-label text-ink/55">
            {intl.formatMessage({
              id: "admin.groupDescriptionEnLabel",
              defaultMessage: "Description (English)",
            })}
          </span>
          <textarea
            className={`mt-1 ${inputCls} min-h-[60px] resize-y`}
            value={descriptionEn}
            onChange={(e) => setDescriptionEn(e.target.value)}
          />
          <span className="field-meta mt-1 block text-ink/45">
            {descriptionEn.length} / {GROUP_DESCRIPTION_TARGET}
          </span>
        </label>
      </div>
      <p className="field-meta mt-2 text-ink/55">
        {intl.formatMessage(
          {
            id: "admin.groupDescriptionHint",
            defaultMessage:
              "Read first on the catalogue tile and above the group in Grow. The tile clamps it, so aim for about {max} characters.",
          },
          { max: GROUP_DESCRIPTION_TARGET },
        )}
      </p>

      <div className="bg-page box-flat mt-4 flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <span className="text-[13px]">
          {intl.formatMessage({
            id: "admin.groupPositionLabel",
            defaultMessage: "Position in Grow · Play",
          })}
          <span className="field-meta mt-0.5 block text-ink/45">
            {intl.formatMessage({
              id: "admin.groupPositionHint",
              defaultMessage: "Where the group itself sits in Grow · Play.",
            })}
          </span>
        </span>
        <NumberStepper
          value={position}
          onChange={(next) => setPosition(Math.round(next))}
          min={0}
          max={MAX_POSITION}
          ariaLabel={intl.formatMessage({
            id: "admin.groupPositionLabel",
            defaultMessage: "Position in Grow · Play",
          })}
        />
      </div>

      <div className="mt-5">
        <span className="field-label text-ink/55">
          {intl.formatMessage({ id: "admin.groupImageLabel", defaultMessage: "Tile image" })}
        </span>
        <FileDropZone
          ref={zoneRef}
          variant="zone"
          className="mt-1"
          accept={RASTER_IMAGE_ACCEPT}
          multiple={false}
          clickToOpen={!preview}
          onFiles={(fl) => void ingest(fl)}
          label={intl.formatMessage({
            id: "admin.groupImageDrop",
            defaultMessage: "Drag an image here or click to upload",
          })}
          hint={intl.formatMessage(
            {
              id: "admin.groupImageLimit",
              defaultMessage: "Max. {max} MB, PNG, JPEG, GIF, BMP, TIFF or WebP",
            },
            { max: maxMb },
          )}
        >
          {preview ? (
            <div className="flex flex-wrap items-center gap-4 p-3">
              <img
                src={preview}
                alt={intl.formatMessage({
                  id: "admin.groupImageAlt",
                  defaultMessage: "Tile image of the group",
                })}
                className="h-20 w-32 shrink-0 rounded-md object-cover"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => zoneRef.current?.openPicker()}>
                  {intl.formatMessage({
                    id: "admin.groupImageReplace",
                    defaultMessage: "Replace",
                  })}
                </Button>
                {confirmingRemoveImage ? (
                  <>
                    <span className="text-[11px] text-ink/55">
                      {intl.formatMessage({
                        id: "admin.groupImageRemoveConfirm",
                        defaultMessage:
                          "Remove image? The tile falls back to the generated artwork.",
                      })}
                    </span>
                    <Button variant="destructive" size="sm" onClick={dropImage}>
                      {intl.formatMessage({
                        id: "admin.yesRemove",
                        defaultMessage: "Yes, remove",
                      })}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirmingRemoveImage(false)}
                    >
                      {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmingRemoveImage(true)}
                  >
                    {intl.formatMessage({
                      id: "admin.groupImageRemove",
                      defaultMessage: "Remove image",
                    })}
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </FileDropZone>
        <p className="field-meta mt-1 text-ink/55">
          {intl.formatMessage({
            id: "admin.groupImageHint",
            defaultMessage:
              "About 16:10, one flat motif, no text and no fine detail. The tile shows it small and sharp.",
          })}
        </p>
        {imageIssue ? <div className="mt-1 text-[12px] text-red-600">{imageIssue}</div> : null}
      </div>

      {group ? (
        <div className="bg-page box-flat mt-5 p-4">
          <div className="mb-2 text-sm font-medium">
            {intl.formatMessage({
              id: "admin.groupMembersTitle",
              defaultMessage: "Questionnaires in this group",
            })}
          </div>
          {members.length === 0 ? (
            <div className="py-2 text-[12px] text-ink/45">
              {intl.formatMessage({
                id: "admin.groupNoMembers",
                defaultMessage: "No questionnaire assigned yet.",
              })}
            </div>
          ) : (
            <ul className="space-y-1">
              {members.map((m, i) => (
                <li key={m.questionnaireId} className="flex items-center gap-2 text-[13px]">
                  <span className="w-5 shrink-0 text-[11px] tabular-nums text-ink/40">{i + 1}.</span>
                  <StatusDot status={String(m.status)} />
                  <span className="min-w-0 flex-1 truncate">{m.name}</span>
                  <Button
                    variant="icon"
                    size="sm"
                    disabled={i === 0 || busy}
                    onClick={() => void move(group.questionnaireGroupId, i, -1)}
                    aria-label={intl.formatMessage({
                      id: "admin.moveUp",
                      defaultMessage: "Move up",
                    })}
                  >
                    <span className="leading-none">↑</span>
                  </Button>
                  <Button
                    variant="icon"
                    size="sm"
                    disabled={i === members.length - 1 || busy}
                    onClick={() => void move(group.questionnaireGroupId, i, 1)}
                    aria-label={intl.formatMessage({
                      id: "admin.moveDown",
                      defaultMessage: "Move down",
                    })}
                  >
                    <span className="leading-none">↓</span>
                  </Button>
                  {removingMemberId === m.questionnaireId ? (
                    <>
                      <span className="text-[11px] text-ink/55">
                        {intl.formatMessage({
                          id: "admin.confirmRemoveFromGroup",
                          defaultMessage:
                            "Remove from the group? It loses its position, and its answers stop counting towards this group's evaluation.",
                        })}
                      </span>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={busy}
                        onClick={() => void removeMember(m.questionnaireId)}
                      >
                        {intl.formatMessage({
                          id: "admin.yesRemove",
                          defaultMessage: "Yes, remove",
                        })}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy}
                        onClick={() => setRemovingMemberId(null)}
                      >
                        {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => setRemovingMemberId(m.questionnaireId)}
                    >
                      {intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="block min-w-[200px] flex-1">
              <span className="field-label text-ink/55">
                {intl.formatMessage({
                  id: "admin.addToGroupLabel",
                  defaultMessage: "Add questionnaire",
                })}
              </span>
              <Select
                className="mt-1"
                value={addSelection}
                onChange={setAddSelection}
                options={assignable}
                placeholder={intl.formatMessage({
                  id: "admin.addToGroupPlaceholder",
                  defaultMessage: "Select questionnaire",
                })}
              />
            </label>
            <Button
              variant="secondary"
              disabled={!addSelection || busy}
              onClick={() => {
                const chosen = allQuestionnaires.find((q) => q.questionnaireId === addSelection);
                if (!chosen) return;
                setPendingAssign({
                  questionnaireId: chosen.questionnaireId,
                  name: chosen.name,
                  fromGroupName: chosen.questionnaireGroupId
                    ? (groupNameById.get(chosen.questionnaireGroupId) ?? null)
                    : null,
                });
              }}
            >
              {intl.formatMessage({ id: "common.add", defaultMessage: "Add" })}
            </Button>
          </div>
        </div>
      ) : null}

      {blockedReason ? (
        <div className="mt-4 text-[12px] text-red-600">{blockedReason}</div>
      ) : null}
      {saveIssue ? <div className="mt-4 text-[12px] text-red-600">{saveIssue}</div> : null}
      {errors.map((e, i) => (
        <div key={i} className="mt-4 text-[12px] text-red-600">
          {userErrorMessage(intl, e)}
        </div>
      ))}

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-ink/5 pt-4">
        <Button variant="primary" onClick={() => void submit()} disabled={!valid || busy}>
          {saving
            ? intl.formatMessage({ id: "common.saving", defaultMessage: "Saving…" })
            : intl.formatMessage({ id: "common.save", defaultMessage: "Save" })}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
        </Button>
        {!valid ? (
          <span className="text-[11px] text-ink/45">
            {intl.formatMessage({
              id: "admin.groupValidationHint",
              defaultMessage: "A name in German and English is required.",
            })}
          </span>
        ) : null}
        {group ? (
          published ? (
            <Button
              variant="secondary"
              onClick={() => setConfirmingUnpublish(true)}
              disabled={busy || consequentialBlocked}
            >
              {intl.formatMessage({ id: "admin.unpublishGroup", defaultMessage: "Withdraw" })}
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                if (group.itemCount > 0 && hasActiveMember(members)) void runPublish();
                else setConfirmingEmptyPublish(true);
              }}
              disabled={busy || consequentialBlocked}
            >
              {intl.formatMessage({ id: "admin.publishGroup", defaultMessage: "Publish" })}
            </Button>
          )
        ) : null}
        {group ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            disabled={busy || consequentialBlocked}
            onClick={() => setConfirmingDelete(true)}
          >
            {intl.formatMessage({ id: "admin.deleteGroup", defaultMessage: "Delete group" })}
          </Button>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title={intl.formatMessage(
          { id: "admin.deleteGroupTitle", defaultMessage: "Delete “{name}”?" },
          { name: group ? groupName(group.name) : "" },
        )}
        message={intl.formatMessage(
          {
            id: "admin.deleteGroupMessage",
            defaultMessage:
              "Deletes the group only. {count, plural, =0 {It holds no questionnaires.} one {Its # questionnaire stays and loses its group and its position.} other {Its # questionnaires stay and lose their group and their position.}} Answers already given are untouched.",
          },
          { count: members.length },
        )}
        confirmLabel={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
        onConfirm={() => void runDelete()}
        onCancel={() => setConfirmingDelete(false)}
      />

      <ConfirmDialog
        open={confirmingUnpublish}
        title={intl.formatMessage(
          { id: "admin.unpublishTitle", defaultMessage: "Withdraw “{name}”?" },
          { name: group ? groupName(group.name) : "" },
        )}
        message={intl.formatMessage({
          id: "admin.unpublishMessage",
          defaultMessage:
            "The group leaves the Grow play catalogue. Answers already given stay and keep counting towards the evaluation.",
        })}
        confirmLabel={intl.formatMessage({
          id: "admin.unpublishGroup",
          defaultMessage: "Withdraw",
        })}
        onConfirm={() => void runUnpublish()}
        onCancel={() => setConfirmingUnpublish(false)}
      />

      <ConfirmDialog
        open={confirmingEmptyPublish}
        destructive={false}
        title={intl.formatMessage({
          id: "admin.publishEmptyTitle",
          defaultMessage: "Publish without an active questionnaire?",
        })}
        message={intl.formatMessage({
          id: "admin.publishEmptyMessage",
          defaultMessage:
            "This group has no active questionnaire, so publishing it still gives the catalogue no tile to show.",
        })}
        confirmLabel={intl.formatMessage({
          id: "admin.publishAnyway",
          defaultMessage: "Publish anyway",
        })}
        onConfirm={() => void runPublish()}
        onCancel={() => setConfirmingEmptyPublish(false)}
      />

      <ConfirmDialog
        open={pendingAssign !== null}
        destructive={false}
        title={intl.formatMessage(
          { id: "admin.rehomeTitle", defaultMessage: "Move “{name}”?" },
          { name: pendingAssign?.name ?? "" },
        )}
        message={
          pendingAssign?.fromGroupName
            ? intl.formatMessage(
                {
                  id: "admin.rehomeFromMessage",
                  defaultMessage:
                    "“{name}” moves out of “{from}” and into “{to}”, at the end of the order. Answers already given move with it and count towards the evaluation of “{to}” from now on.",
                },
                {
                  name: pendingAssign.name,
                  from: pendingAssign.fromGroupName,
                  to: group ? groupName(group.name) : "",
                },
              )
            : intl.formatMessage(
                {
                  id: "admin.rehomeMessage",
                  defaultMessage:
                    "“{name}” is placed at the end of “{to}”. Answers already given count towards the evaluation of “{to}” from now on.",
                },
                { name: pendingAssign?.name ?? "", to: group ? groupName(group.name) : "" },
              )
        }
        confirmLabel={intl.formatMessage({ id: "admin.rehomeConfirm", defaultMessage: "Move" })}
        onConfirm={() => void confirmAssign()}
        onCancel={() => setPendingAssign(null)}
      />
    </div>
  );
}
